import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.prima.myx.is';

export interface Correction {
  original: string;
  suggestion: string;
  startChar: number;
  endChar: number;
  code: string;
  category: string;
  label: string;
  description: string;
}

interface ProofreadResult {
  corrections: Correction[];
  correctedText: string;
  hasErrors: boolean;
  apiError?: boolean;
}

export interface UseProofreaderReturn {
  corrections: Correction[];
  correctedText: string;
  isChecking: boolean;
  hasErrors: boolean;
  applyCorrection: (correction: Correction) => string;
  applyAll: () => string;
}

/**
 * Debounced Icelandic proofreading hook.
 * Calls /api/ai/proofread after user stops typing.
 */
export function useProofreader(
  text: string,
  token: string | null,
  delay: number = 800,
  minLength: number = 5
): UseProofreaderReturn {
  const [result, setResult] = useState<ProofreadResult>({
    corrections: [],
    correctedText: text,
    hasErrors: false,
  });
  const [isChecking, setIsChecking] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!text || text.trim().length < minLength || !token) {
      setResult({ corrections: [], correctedText: text, hasErrors: false });
      setIsChecking(false);
      return;
    }

    setIsChecking(true);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${API_URL}/api/ai/proofread`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ text }),
          signal: controller.signal,
        });

        if (!response.ok) {
          setIsChecking(false);
          return;
        }

        const json = await response.json() as { success: boolean; data?: ProofreadResult };
        if (json.success && json.data) {
          setResult(json.data);
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.warn('Stafsetningarprófun mistókst:', error);
        }
      } finally {
        setIsChecking(false);
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [text, token, delay, minLength]);

  const applyCorrection = useCallback((correction: Correction): string => {
    if (!correction.original || !correction.suggestion) return text;
    return text.replace(correction.original, correction.suggestion);
  }, [text]);

  const applyAll = useCallback((): string => {
    let out = text;
    const sorted = [...result.corrections].sort((a, b) => (b.startChar || 0) - (a.startChar || 0));
    for (const c of sorted) {
      if (c.original && c.suggestion) {
        out = out.replace(c.original, c.suggestion);
      }
    }
    return out;
  }, [text, result.corrections]);

  return {
    corrections: result.corrections,
    correctedText: result.correctedText,
    isChecking,
    hasErrors: result.hasErrors,
    applyCorrection,
    applyAll,
  };
}
